// File Upload & Storage Service — Azure Blob / Local fallback
// All file access uses time-limited signed URLs (SAS tokens in production).
import api from './api';

// In-memory SAS URL cache to avoid redundant API calls during a session.
// Entries auto-expire based on TTL from the server response.
const sasCache = new Map();

function getCachedUrl(key) {
  const entry = sasCache.get(key);
  if (!entry) return null;
  if (Date.now() > entry.expiresAt) {
    sasCache.delete(key);
    return null;
  }
  return entry.url;
}

function setCachedUrl(key, url, ttlSeconds) {
  // Cache for 80% of TTL to ensure URL is still valid when used
  const bufferMs = ttlSeconds * 800;
  sasCache.set(key, { url, expiresAt: Date.now() + bufferMs });
}

export const fileService = {
  // Upload a file for a patient
  async uploadFile(file, patientId, documentType, description = '') {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('patient_id', patientId);
      formData.append('document_type', documentType);
      if (description) {
        formData.append('description', description);
      }

      const response = await api.post('/api/v1/files/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to upload file',
      };
    }
  },

  // Get all files for a patient
  async getPatientFiles(patientId, documentType = '') {
    try {
      const params = documentType ? `?document_type=${documentType}` : '';
      const response = await api.get(`/api/v1/files/patient/${patientId}${params}`);
      return { success: true, data: response.data.documents };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to fetch files',
      };
    }
  },

  // Delete a file
  async deleteFile(documentId) {
    try {
      await api.delete(`/api/v1/files/${documentId}`);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to delete file',
      };
    }
  },

  /**
   * Get a time-limited signed URL for viewing a document inline (5 min TTL).
   * Uses in-memory cache to avoid redundant API calls.
   * @param {string} documentId - The document ID
   * @returns {{ success: boolean, url?: string, error?: string }}
   */
  async getSignedUrl(documentId, purpose = 'view') {
    const cacheKey = `${documentId}:${purpose}`;
    const cached = getCachedUrl(cacheKey);
    if (cached) return { success: true, url: cached };

    try {
      const response = await api.get(`/api/v1/files/signed-url/${documentId}?purpose=${purpose}`);
      const { signed_url, expires_in } = response.data;
      if (signed_url) {
        setCachedUrl(cacheKey, signed_url, expires_in || 300);
      }
      return { success: true, url: signed_url };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to get file URL',
      };
    }
  },

  /**
   * Get a signed URL for downloading a document (15 min TTL).
   * @param {string} documentId - The document ID
   * @returns {{ success: boolean, url?: string }}
   */
  async getDownloadUrl(documentId) {
    return this.getSignedUrl(documentId, 'download');
  },

  /**
   * Get a signed URL for a user's avatar image (30 min TTL, cached).
   * @param {string} userId - The user ID
   * @returns {{ success: boolean, url?: string }}
   */
  async getAvatarUrl(userId) {
    const cacheKey = `avatar:${userId}`;
    const cached = getCachedUrl(cacheKey);
    if (cached) return { success: true, url: cached };

    try {
      const response = await api.get(`/api/v1/files/avatar-url/${userId}`);
      const { signed_url, expires_in } = response.data;
      if (signed_url) {
        setCachedUrl(cacheKey, signed_url, expires_in || 1800);
      }
      return { success: true, url: signed_url };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to get avatar URL',
      };
    }
  },

  /**
   * Clear the SAS URL cache (e.g., after uploading a new file or avatar).
   * @param {string} [key] - Optional specific cache key to clear. Clears all if omitted.
   */
  clearCache(key) {
    if (key) {
      // Clear all entries matching this key prefix
      for (const k of sasCache.keys()) {
        if (k.startsWith(key)) sasCache.delete(k);
      }
    } else {
      sasCache.clear();
    }
  },

  // Get notifications for current user
  async getNotifications(unreadOnly = false) {
    try {
      const params = unreadOnly ? '?unread_only=true' : '';
      const response = await api.get(`/api/v1/files/notifications${params}`);
      return { success: true, data: response.data.notifications };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to fetch notifications',
      };
    }
  },

  // Mark notification as read
  async markNotificationRead(notificationId) {
    try {
      await api.post(`/api/v1/files/notifications/${notificationId}/read`);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to mark notification as read',
      };
    }
  },
};

export default fileService;
